import { createHttpClient } from '@adapters/HttpClient';
import { LocationsApiAdapter } from '@modules/Products/CreateEdit/adapters/LocationsApiAdapter';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export function useProductStockLocations(productId: string | number | null, searchTerm: string, enabled: boolean) {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new LocationsApiAdapter(httpClient), [httpClient]);
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);

  const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['locations-by-product', productId, debouncedSearch],
    enabled: enabled && Boolean(productId),
    queryFn: ({ pageParam = 1 }) =>
      adapter.getPaginated({
        product_ids: String(productId),
        page: pageParam,
        per_page: PAGE_SIZE,
        search: debouncedSearch || undefined,
        order_direction: 'desc',
      }),
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length + 1 : undefined),
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const locations = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const total = data?.pages[0]?.total ?? 0;

  return {
    locations,
    total,
    isLoading: isLoading || (isFetching && locations.length === 0),
    error,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
}
